import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import * as _ from 'underscore';
import { Api } from 'src/app/shared/constants/api';
import { Message } from 'src/app/shared/constants/message';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { IPermissionWithShow } from 'src/app/shared/interface';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { ICountryWiseServiceRequirement, ICountryWiseServiceRequirementCriteria } from '../country-wise-service-requirement.interface';

@Component({
    selector: 'app-country-wise-service-requirement-list',
    templateUrl: './country-wise-service-requirement-list.component.html',
    styleUrls: ['./country-wise-service-requirement-list.component.less'],
    standalone: false
})
export class CountryWiseServiceRequirementListComponent implements OnInit, AfterViewInit, OnDestroy {

  totalRecords: any;
  columns = ['countryName', 'businessSegmentName', 'action'];
  pageSizeOptions: number[] = this.utilityService.getPageSizeOptions();
  dataSource = new MatTableDataSource<ICountryWiseServiceRequirement>();
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  countryWiseServiceRequirementListForm: FormGroup;
  criteria: ICountryWiseServiceRequirementCriteria = {};
  addPermission: boolean = false;
  editPermission: boolean = false;
  viewPermission: boolean = false;
  subscription = new Subscription();

  constructor(private logger: NGXLogger, private service: DataService,
    private router: Router, private activatedRoute: ActivatedRoute,
    private utilityService: UtilityService, private formBuilder: FormBuilder,
    private permissionService: PermissionService) { }

  ngOnInit(): void {
    this.getAllPermissions();
    this.initializeForm();
    this.setCountryNameValueChangeEvent();
    this.setBusinessSegmentNameValueChangeEvent();
  }

  ngAfterViewInit() {
    // Server-side pagination: do NOT attach paginator/sort to the MatTableDataSource,
    // otherwise it overwrites paginator.length with the current page size (e.g. 20),
    // which disables the next-page arrow and hides remaining records. The paginator is
    // driven by [length]="totalRecords" and the (page) event below.
    this.paginator?.page?.pipe(tap(() => this.getList())).subscribe();
    this.getList();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private setCountryNameValueChangeEvent() {
    const sub = this.countryName.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(value => {
      this.criteria.countryName = value;
      this.getList(true);
    });
    this.subscription.add(sub);
  }

  private setBusinessSegmentNameValueChangeEvent() {
    const sub = this.businessSegmentName.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(value => {
      this.criteria.businessSegmentName = value;
      this.getList(true);
    });
    this.subscription.add(sub);
  }

  initializeForm() {
    this.countryWiseServiceRequirementListForm = this.formBuilder.group({
      countryName: [],
      businessSegmentName: []
    });
  }

  sortData(event: any) {
    this.criteria.sort = [event.active, event.direction];
    this.getList(true);
  }

  openForm(id?: number) {
    if (id) {
      this.editPermission
        ? this.router.navigate(['edit/' + id], { relativeTo: this.activatedRoute })
        : this.utilityService.notifier(Message.EDIT_PERMISSION_WARNING, 'warning');
    } else {
      this.addPermission
        ? this.router.navigate(['add'], { relativeTo: this.activatedRoute })
        : this.utilityService.notifier(Message.EDIT_PERMISSION_WARNING, 'warning');
    }
  }

  getList(resetPageIndex: boolean = false) {
    if (resetPageIndex) {
      this.paginator.pageIndex = 0;
    }
    this.criteria.page = this.paginator?.pageIndex;
    this.criteria.size = this.paginator?.pageSize;
    this.criteria.sort = this.criteria.sort ? this.criteria.sort : ['createdDate', 'desc'];
    if (this.viewPermission) {
      this.service.get(Api.COUNTRY_WISE_SERVICE_REQUIREMENT, this.criteria).subscribe(res => {
        if (res) {
          if (res.contents.length === 0) {
            this.utilityService.notifier('No records found', 'info');
          }
          this.logger.info('Country Wise Service Requirement List', res);
          this.dataSource.data = res.contents;
          this.totalRecords = res.totalRecords;
          if (this.paginator.pageIndex >= Math.ceil(this.totalRecords / this.paginator.pageSize)) {
            this.paginator.pageIndex = 0;
          }
        }
      });
    } else {
      this.utilityService.notifier(Message.VIEW_PERMISSION_WARNING, 'warning');
    }
  }

  public getAllPermissions() {
    const permissionCodes = [
      PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE_ADD_PERMISSION,
      PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE_EDIT_PERMISSION,
      PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE_VIEW_PERMISSION
    ];
    const permissionsWithShowValue: IPermissionWithShow[] =
      this.permissionService.checkPermissions(permissionCodes, PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE);
    this.addPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE_ADD_PERMISSION })?.show ? true : false;
    this.editPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE_EDIT_PERMISSION })?.show ? true : false;
    this.viewPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.COUNTRY_WISE_SERVICE_REQUIREMENT_MODULE_VIEW_PERMISSION })?.show ? true : false;
  }

  get countryName() { return this.countryWiseServiceRequirementListForm.get('countryName'); }
  get businessSegmentName() { return this.countryWiseServiceRequirementListForm.get('businessSegmentName'); }
}
